// @ts-nocheck
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { ShipmentStatusModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SearchFieldWrapper, {
    SearchFieldWrapperStyles,
} from "@insite/content-library/Widgets/OrderHistory/SearchFieldWrapper";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox";
import CheckboxGroup from "@insite/mobius/CheckboxGroup";
import Clickable from "@insite/mobius/Clickable";
import { IconMemo, IconProps } from "@insite/mobius/Icon";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import InfoFill from "@store/Library/Components/Toolkit/Icons/Info/InfoFill";
import { loadingDotsStyles } from "@store/Themes/common/mixins/typography";
import constants from "@store/constants";
import { getShipmentStatusesDataView } from "@store/Framework/Store/Greif/Data/ShipmentStatusMapping/ShipmentStatusesSelectors";
import updateSearchFields from "@store/Framework/Store/Greif/Pages/ShipmentHistory/Handlers/UpdateSearchFields";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import parse from "html-react-parser";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showShipmentStatusDetails = "showShipmentStatusDetails",
    shipmentStatusDetails = "shipmentStatusDetails",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showShipmentStatusDetails]: boolean;
        [fields.shipmentStatusDetails]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    shipmentStatusMappingsDateView: getShipmentStatusesDataView(state),
    deliveryStatuses: state.greif.pages.shipmentHistory.getShipmentsParameter.deliveryStatuses,
});

const mapDispatchToProps = {
    updateSearchFields,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ShipmentHistorySearchFieldStatusStyles {
    wrapper?: SearchFieldWrapperStyles;
    statusLabelWrapper?: InjectableCss;
    actionIcon?: IconProps;
    statusDetailsModal?: ModalPresentationProps;
    loadingText?: TypographyPresentationProps;
}

export const statusStyles: ShipmentHistorySearchFieldStatusStyles = {
    statusLabelWrapper: {
        css: css`
            display: flex;
            align-items: center;
            gap: ${pxToRem(8)};
        `,
    },
    actionIcon: {
        src: InfoFill,
        size: 20,
    },
    loadingText: {
        css: loadingDotsStyles(),
    },
};
const styles = statusStyles;

const ShipmentHistorySearchFieldStatus = ({
    fields: { showShipmentStatusDetails, shipmentStatusDetails },
    shipmentStatusMappingsDateView,
    deliveryStatuses,
    updateSearchFields,
}: Props) => {
    const { value: shipmentStatusValues, isLoading } = shipmentStatusMappingsDateView;

    const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);
    const [uniqueOptions, setUniqueOptions] = useState<Map<string, string[]>>();

    useEffect(() => {
        if (shipmentStatusValues && !isLoading) {
            const options = shipmentStatusValues || [];
            const uniqueOptions = new Map<string, string[]>();

            for (const option of options) {
                const shipmentStatuses = uniqueOptions.get(option.description) || [];
                shipmentStatuses.push(option.name);
                uniqueOptions.set(option.description, shipmentStatuses);
            }

            setUniqueOptions(uniqueOptions);
        }
    }, [shipmentStatusValues]);

    const toggleStatusDetailsModal = () => {
        setModalIsOpen(!modalIsOpen);
    };

    let filteredStatuses: string[] = [];

    const handleChange = (deliveryStatus: { name: string; values: string[] }) => {
        const _deliveryStatuses: any[] = [...(deliveryStatuses || [])];
        const statusIndex = _deliveryStatuses?.indexOf(deliveryStatus.values[0]);

        if (statusIndex === -1) {
            filteredStatuses = _deliveryStatuses.concat(deliveryStatus.values[0]);
        }

        if (statusIndex >= 0) {
            _deliveryStatuses.splice(statusIndex, 1);
            filteredStatuses = _deliveryStatuses;
        }

        updateSearchFields({ deliveryStatuses: filteredStatuses });
    };

    const shipmentStatusDescTitle = translate("Shipment Status Descriptions");

    return (
        <SearchFieldWrapper extendedStyles={styles.wrapper}>
            <CheckboxGroup
                label={
                    <StyledWrapper {...styles.statusLabelWrapper}>
                        {translate("Shipment Status")}
                        {showShipmentStatusDetails && (
                            <Clickable onClick={toggleStatusDetailsModal}>
                                <IconMemo {...styles.actionIcon} title={shipmentStatusDescTitle} />
                            </Clickable>
                        )}
                    </StyledWrapper>
                }
            >
                <LoadingOverlay loading={isLoading}>
                    {uniqueOptions &&
                        Array.from(uniqueOptions, ([name, values]) => ({ name, values })).map(option => (
                            <Checkbox
                                key={option.values.join(",")}
                                checked={
                                    (deliveryStatuses && deliveryStatuses?.indexOf(option.values[0]) > -1) || false
                                }
                                onChange={() => handleChange(option)}
                                data-status={option.values}
                            >
                                {option.name}
                            </Checkbox>
                        ))}
                </LoadingOverlay>
            </CheckboxGroup>
            <Modal
                headline={shipmentStatusDescTitle}
                isOpen={modalIsOpen}
                handleClose={toggleStatusDetailsModal}
                {...styles.statusDetailsModal}
            >
                {parse(shipmentStatusDetails, parserOptions)}
            </Modal>
        </SearchFieldWrapper>
    );
};

function findDisplayName(shipmentStatus: string, shipmentStatusMappings: ShipmentStatusModel[] = []) {
    const arrOfStatus = shipmentStatus.split(",");

    let returnVal;
    for (let i = 0; i < shipmentStatusMappings.length; i++) {
        const option = shipmentStatusMappings[i];
        if (arrOfStatus.includes(option.name)) {
            returnVal = option.description;
            break;
        }
    }
    return returnVal;
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ShipmentHistorySearchFieldStatus),
    definition: {
        group: constants.widgetGroup.shipmentHistory,
        displayName: "Status",
        allowedContexts: [constants.pageType.shipmentHistory],
        fieldDefinitions: [
            {
                name: fields.showShipmentStatusDetails,
                fieldType: "Translatable",
                editorTemplate: "CheckboxField",
                defaultValue: true,
            },
            {
                name: fields.shipmentStatusDetails,
                fieldType: "Translatable",
                editorTemplate: "RichTextField",
                defaultValue:
                    "<ul> <li> Confirmed – shipment planning underway </li><li> Pending Carrier Pick up – the shipment is ready for delivery, waiting for the carrier pick up</li><li> Ready for Pick Up – Greif shipment is prepared and ready to be picked up</li><li> In Transit – the Greif carrier has departed the plant, your goods are on their way, delivery tracking is available</li><li> Delivered – the goods have been delivered to shipping address</li></ul>",
            },
        ],
    },
};

export default widgetModule;
